import * as React from "react";
import { alpha, styled } from "@mui/material/styles";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import TextArea from "antd/lib/input/TextArea";
import {
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Select,
} from "@mui/material";
import InputBase from "@mui/material/InputBase";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { marketingFormFieldSx } from "./tickitMarketingTheme";
import { platformFormFieldSx } from "./tickitPlatformTheme";

export const main_button = {
  color: "#fff",
  bgcolor: "#46109B",
  fontFamily: "'Syne', 'TickitOne', Helvetica, Arial, sans-serif",
  fontWeight: 800,
  boxShadow: "0 2px 8px rgba(70,16,155,0.3)",
  ":hover": {
    color: "#fff",
    bgcolor: "#5A1EC8",
    boxShadow: "0 6px 20px rgba(70,16,155,0.35)",
  },
  ":disabled": {
    color: "#6B5E80",
    bgcolor: "#E4E0EB",
  },
  borderRadius: "12px",
  padding: 1,
  textTransform: "none",
};

export const sec_button = {
  color: "#46109B",
  bgcolor: "white",
  border: "2px solid #E0D4FC",
  fontFamily: "'Syne', 'TickitOne', Helvetica, Arial, sans-serif",
  fontWeight: 700,
  ":hover": {
    color: "#46109B",
    bgcolor: "#F0EAFF",
    borderColor: "#C4A8F8",
  },
  ":disabled": {
    color: "#9488A8",
    bgcolor: "#F8F7FA",
    borderColor: "#E4E0EB",
  },
  borderRadius: "12px",
  padding: 1,
  textTransform: "none",
};

export const ter_button = {
  color: "var(--mainWhite)",
  bgcolor: "transparent",
  border: "1px solid var(--primaryColor)",
  fontFamily: "TickitOne, Helvetica, Arial, sans-serif",
  ":hover": {
    color: "var(--mainWhite)",
    bgcolor: "var(--primaryColor)",
  },
  ":disabled": {
    color: "#333",
    bgcolor: "transparent",
    borderColor: "#e0e0e0",
  },
  borderRadius: "8px",
  fontWeight: "bold",
  padding: 1,
  textTransform: "none",
};

export const confirm_button = {
  color: "var(--mainWhite)",
  bgcolor: "#10b981",
  fontFamily: "TickitOne, Helvetica, Arial, sans-serif",
  ":hover": {
    bgcolor: "#059669",
    color: "var(--mainWhite)",
  },
  ":disabled": {
    color: "#333",
    bgcolor: "#e0e0e0",
  },
  borderRadius: "8px",
  fontWeight: "bold",
  padding: 1,
  textTransform: "none",
};

export const reject_button = {
  color: "var(--mainWhite)",
  bgcolor: "#ef4444",
  fontFamily: "TickitOne, Helvetica, Arial, sans-serif",
  ":hover": {
    bgcolor: "#dc2626",
    color: "var(--mainWhite)",
  },
  ":disabled": {
    color: "#333",
    bgcolor: "#e0e0e0",
  },
  borderRadius: "8px",
  fontWeight: "bold",
  padding: 1,
  textTransform: "none",
};

export const card_style = {
  padding: 2,
  marginY: 2,
  marginX: 2,
  backgroundColor: "white",
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  ":hover": {
    transition: "all 0.3s ease",
    boxShadow: "0 8px 25px rgba(147, 51, 234, 0.15)",
    borderColor: "var(--primaryPurple)",
    cursor: "pointer",
  },
};

export const centered_flex_box = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const left_flex_box = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
};

export const right_flex_box = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
};

export const MainInput = styled(TextField)(marketingFormFieldSx);

/** Theme-aware fields for platform admin pages (dark mode via CSS variables) */
export const PlatformMainInput = styled(TextField)(platformFormFieldSx);

export const SecInput = styled(TextField)(marketingFormFieldSx);

export const TerInput = styled(TextField)(marketingFormFieldSx);

export const MainTextArea = ({
  name,
  autoComplete,
  autoFocus,
  label,
  id,
  required,
  lines,
  defaultValue,
  value,
  onChange,
  inputRef,
  minRows,
  ...rest
}) => {
  const internalRef = React.useRef(null);

  React.useEffect(() => {
    if (inputRef && internalRef.current && internalRef.current.resizableTextArea) {
      inputRef.current = internalRef.current.resizableTextArea.textArea;
    }
  }, [inputRef]);

  return (
    <>
      <InputLabel sx={{ 
        color: "var(--platform-text-2, #333)", 
        fontFamily: "TickitTwo, Helvetica, Arial, sans-serif",
        mb: 1
      }}>
        {label}
      </InputLabel>
      <TextArea
        ref={internalRef}
        name={name}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        autoFocus={autoFocus}
        id={id}
        aria-label={label}
        required={required}
        value={value}
        onChange={onChange}
        rows={minRows || lines}
        style={{
          borderRadius: "8px",
          fontFamily: "TickitTwo, Helvetica, Arial, sans-serif",
          backgroundColor: "var(--platform-surface-2, #ffffff)",
          color: "var(--platform-text, var(--primaryPurple))",
          border: "1px solid var(--platform-border-2, #e0e0e0)",
          padding: "12px",
          fontSize: "16px",
          height: lines && !minRows ? `${lines * 24}px` : undefined,
          resize: "vertical",
        }}
        {...rest}
      />
    </>
  );
};

export const MainPassword = ({
  name,
  autoComplete,
  fullWidth,
  label,
  id,
  required,
  margin,
  sx,
  value,
  readOnly,
  error,
  type = "password",
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <FormControl sx={{ width: "100%", ...sx }} variant="outlined">
      <InputLabel
        htmlFor="outlined-adornment-password"
        sx={{
          ...marketingFormFieldSx["& .MuiInputLabel-root"],
          "&.Mui-focused": { color: "#46109B !important" },
        }}
      >
        {label}
        {required ? "*" : ""}
      </InputLabel>
      <OutlinedInput
        id={id}
        name={name}
        margin={margin}
        required={required}
        autoComplete={autoComplete}
        fullWidth={fullWidth}
        type={showPassword ? "text" : type}
        readOnly={readOnly}
        value={value}
        error={error}
        sx={{
          ...marketingFormFieldSx["& .MuiOutlinedInput-root"],
          "& .MuiInputBase-input": marketingFormFieldSx["& .MuiInputBase-input"],
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showPassword ? (
                <VisibilityOff sx={{ color: "#333" }} />
              ) : (
                <Visibility sx={{ color: "#333" }} />
              )}
            </IconButton>
          </InputAdornment>
        }
        label={label}
      />
    </FormControl>
  );
};

export const SecPassword = ({
  name,
  autoComplete,
  fullWidth,
  label,
  id,
  required,
  margin,
  sx,
  value,
  readOnly,
  error,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <FormControl sx={{ width: "100%", ...sx }} variant="outlined">
      <InputLabel
        sx={{ 
          color: "#333",
          fontFamily: "TickitTwo, Helvetica, Arial, sans-serif",
          "&.Mui-focused": {
            color: "var(--primaryColor)"
          }
        }}
        htmlFor="outlined-adornment-password"
      >
        {label}
        {required ? "*" : ""}
      </InputLabel>
      <OutlinedInput
        sx={{
          backgroundColor: "white",
          "& .MuiInputBase-input": {
            color: "var(--primaryPurple)",
            fontFamily: "TickitTwo, Helvetica, Arial, sans-serif",
          },
          "& fieldset": {
            border: "1px solid var(--primaryColor)",
            borderRadius: "8px",
          },
          "&.Mui-focused fieldset": {
            borderColor: "var(--primaryColor)",
          },
          "&:hover fieldset": {
            borderColor: "var(--primaryColor)",
          },
        }}
        id={id}
        name={name}
        margin={margin}
        required={required}
        autoComplete={autoComplete}
        fullWidth={fullWidth}
        type={showPassword ? "text" : "password"}
        readOnly={readOnly}
        value={value}
        error={error}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showPassword ? (
                <VisibilityOff sx={{ color: "#333" }} />
              ) : (
                <Visibility sx={{ color: "#333" }} />
              )}
            </IconButton>
          </InputAdornment>
        }
        label={label}
      />
    </FormControl>
  );
};

export const MainInputLabel = ({ required, id, title, color = "#333" }) => {
  return (
    <InputLabel 
      sx={{ 
        color,
        fontFamily: "TickitTwo, Helvetica, Arial, sans-serif",
        mb: 1
      }} 
      required={required} 
      id={id}
    >
      {title}
    </InputLabel>
  );
};
